import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Divider,
  IconButton,
  Grid,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { Booking } from "src/business/booking";
import { BookingStatusBadge } from "./BookingStatusBadge";
import { NoShowWarningIndicator } from "./NoShowWarningIndicator";
import {
  colorBorder,
  colorSurface,
  colorTextMuted,
  colorTextPrimary,
  colorTextSecondary,
  colorTextDisabled,
  colorSurfaceHover,
  radiusCard,
  radiusInput,
  shadowCard,
} from "src/design/tokens";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

interface Props {
  booking: Booking;
  open: boolean;
  onClose: () => void;
}

const FieldRow = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) => (
  <Box>
    <Typography sx={{ fontSize: "11px", fontWeight: 500, color: colorTextMuted, mb: 0.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>
      {label}
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Box sx={{ color: colorTextMuted, display: "flex", alignItems: "center" }}>{icon}</Box>
      <Typography sx={{ fontSize: "14px", color: colorTextPrimary, fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

export const BookingDetailsModal = ({ booking, open, onClose }: Props) => {
  const { t } = useTranslation();
  const bookingDay = dayjs(booking.bookingDate);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: radiusCard,
          boxShadow: shadowCard,
          border: `1px solid ${colorBorder}`,
        },
      }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "18px", lineHeight: 1.3 }}>
              {t("bookings.detailsModal.title")}
            </Typography>
            <Typography sx={{ fontSize: "13px", color: colorTextMuted, mt: 0.25 }}>
              {t("bookings.detailsModal.subtitle")}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ mt: -0.5, mr: -1 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider sx={{ mx: 3, mt: 1.5 }} />

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "16px", color: colorTextPrimary }}>
            {booking.userName}
          </Typography>
          <BookingStatusBadge status={booking.bookingStatus} />
          {booking.hasNoShowWarning && (
            <NoShowWarningIndicator count={booking.noShowCount} />
          )}
        </Box>

        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          <Grid item xs={6}>
            <FieldRow
              icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 15 }} />}
              label={t("bookings.detailsModal.date")}
              value={bookingDay.format("ddd, MMM D, YYYY")}
            />
          </Grid>
          <Grid item xs={6}>
            <FieldRow
              icon={<AccessTimeOutlinedIcon sx={{ fontSize: 15 }} />}
              label={t("bookings.detailsModal.time")}
              value={booking.bookingHour}
            />
          </Grid>
          <Grid item xs={6}>
            <FieldRow
              icon={<PeopleOutlineIcon sx={{ fontSize: 15 }} />}
              label={t("bookings.detailsModal.partySize")}
              value={`${booking.numberOfSeats} ${t("bookings.list.guests")}`}
            />
          </Grid>
          <Grid item xs={6}>
            <FieldRow
              icon={<PhoneOutlinedIcon sx={{ fontSize: 15 }} />}
              label={t("bookings.detailsModal.phone")}
              value={booking.phone}
            />
          </Grid>
          <Grid item xs={12}>
            <FieldRow
              icon={<EmailOutlinedIcon sx={{ fontSize: 15 }} />}
              label={t("bookings.detailsModal.email")}
              value={booking.email}
            />
          </Grid>
        </Grid>

        {booking.dealContent && (
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: "11px", fontWeight: 500, color: colorTextMuted, mb: 0.75, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              {t("bookings.detailsModal.deal")}
            </Typography>
            <Box
              sx={{
                backgroundColor: colorSurface,
                border: `1px solid ${colorBorder}`,
                borderRadius: radiusInput,
                px: 2,
                py: 1.5,
              }}
            >
              <Typography sx={{ fontSize: "13px", color: colorTextSecondary }}>
                {booking.dealContent}
              </Typography>
            </Box>
          </Box>
        )}

        <Typography sx={{ fontSize: "12px", color: colorTextMuted, mb: 2 }}>
          {t("bookings.detailsModal.createdAt", {
            date: bookingDay.format("MMMM D, YYYY"),
          })}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              borderColor: colorBorder,
              color: colorTextPrimary,
              borderRadius: "8px",
              "&:hover": { borderColor: colorTextDisabled, backgroundColor: colorSurfaceHover },
            }}
          >
            {t("bookings.detailsModal.close")}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
